/**
 * Copy / CSV of the Rules tables as they are shown (review #790): the lines on screen,
 * with the figures beside them, filters and sort applied. The ⋯ menu's CSV is a
 * different thing — the saved rule rows, for import back — and stays as it is.
 *
 * Each tab maps its lines to one of these plain rows, so the columns can be tested
 * without the page.
 */
import type { ExportColumn } from '@/ui/export'

/* ------------------------------------------------------------------ payer types */

export interface PayerTypeExportRow {
  payerType: string
  /** Null: a saved payer type the data loaded does not have. */
  resolved: number | null
  /** 0% touch share at its limit, a fraction. */
  share: number | null
  /** No-touch claims over the limit; null with no limit. */
  over: number | null
  /** Days; null is no limit. */
  limit: number | null
  reason: string | null
}

export const PAYER_TYPE_EXPORT: readonly ExportColumn<PayerTypeExportRow>[] = [
  { header: 'Payer type', value: (r) => r.payerType },
  { header: 'Resolved claims', value: (r) => r.resolved },
  { header: '0% touch share', value: (r) => r.share },
  { header: 'Over the limit', value: (r) => r.over },
  {
    header: 'Over the limit %',
    value: (r) => (r.over === null || !r.resolved ? null : r.over / r.resolved),
  },
  { header: 'Limit (days)', value: (r) => (r.limit === null ? 'No limit' : r.limit) },
  { header: 'Reason', value: (r) => r.reason },
]

/* ------------------------------------------------------------------ payer lists */

export type LineRole = 'main' | 'state' | 'sub' | 'history'

/** A line of the coinsurance or hard payer list: a payer, one state's setting, a dated change. */
export interface PayerExportLine {
  payer: string
  /** Hard payers: a pattern rule ("payer name contains …"), not one payer. */
  pattern?: boolean
  role: LineRole
  state: string | null
  /** ISO bill date the line is in force from. */
  from: string | null
  /** Every payer type the payer is billed under; the payer's own line only. */
  payerTypes: readonly string[]
  claims: number | null
}

const LINE_KIND: Record<LineRole, string> = {
  main: 'Payer',
  state: 'One state',
  sub: 'Dated change',
  history: 'History',
}

function lineColumns<T extends PayerExportLine>(): ExportColumn<T>[] {
  return [
    { header: 'Payer', value: (r) => (r.pattern ? `Payer name matches ${r.payer}` : r.payer) },
    { header: 'Line', value: (r) => LINE_KIND[r.role] },
    { header: 'State', value: (r) => r.state },
    { header: 'From', value: (r) => r.from },
    { header: 'Payer type', value: (r) => (r.role === 'main' ? r.payerTypes.join(', ') : null) },
    { header: 'Claims', value: (r) => r.claims },
  ]
}

const yesNo = (b: boolean) => (b ? 'Yes' : 'No')

export interface CoinsPayerExportRow extends PayerExportLine {
  includeForHmo: boolean
  includeForMedicare: boolean
  weight: number
  status: 'Set' | 'Default' | 'Not saved'
}

export const COINS_PAYER_EXPORT: readonly ExportColumn<CoinsPayerExportRow>[] = [
  ...lineColumns<CoinsPayerExportRow>(),
  { header: 'Counts for HMO', value: (r) => yesNo(r.includeForHmo) },
  { header: 'Counts for Medicare', value: (r) => yesNo(r.includeForMedicare) },
  { header: 'Weight', value: (r) => r.weight },
  { header: 'Status', value: (r) => r.status },
]

export interface HardPayerExportRow extends PayerExportLine {
  /** The weight in force on the line: its own when set, else what it inherits (1 for a normal payer). */
  weight: number
  /** Whether the line has its own weight saved. */
  set: boolean
  reason: string | null
}

export const HARD_PAYER_EXPORT: readonly ExportColumn<HardPayerExportRow>[] = [
  ...lineColumns<HardPayerExportRow>(),
  { header: 'Weight', value: (r) => r.weight },
  { header: 'Set', value: (r) => yesNo(r.set) },
  { header: 'Reason', value: (r) => r.reason },
]
